import { forwardRef, ReactNode, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import Toggle from '@r1-frontend/ui-react/components/controls/Toggle';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import { getVideocontrolItems, IShopItem } from '~/src/api/content/shop/getShopItems';
import {
    dataCameraPlans,
    dataLocation,
    dataLocationFlat,
    dataTabsForLoc,
    ELocation,
} from '~/src/features/videocontrol/data';
import { CameraCard } from '~/src/features/videocontrol/pageBlocks/StepsInstall/CameraCard';
import { widthCard } from '~/src/features/videocontrol/pageBlocks/StepsInstall/constants';
import { LocationTabsBlock } from '~/src/features/videocontrol/pageBlocks/StepsInstall/LocationTabsBlock';
import { NukaWrapper } from '~/src/features/videocontrol/pageBlocks/StepsInstall/NukaWrapper';
import { PlanCard } from '~/src/features/videocontrol/pageBlocks/StepsInstall/PlanCard';
import { StepTitle } from '~/src/features/videocontrol/pageBlocks/StepsInstall/StepTitle';
import { useRequisitionContext } from '~/src/features/videocontrol/Provider/useRequisitionContext';
import { TState } from '~/src/store';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import * as ST from './styled';

interface IProps {
    children?: ReactNode,
    domain: string,
}

type TVideoCamerasDataAccumulator = {
    flatAndHome: IShopItem[],
    entrance: IShopItem[],
};

const StepsInstall = forwardRef<HTMLHeadingElement, IProps>((props, ref): JSX.Element => {
    const defaultTab = 0;
    const [activeTabToggleLoc, setActiveTabToggleLoc] = useState(dataTabsForLoc.leftText);
    const [activeTabLocation, setActiveTabLocation] = useState<ELocation>(dataLocationFlat[defaultTab].id);
    const [currentLocationData, setCurrentLocationData] = useState(dataLocationFlat);
    const [videoCamerasData, setVideoCamerasData] = useState<TVideoCamerasDataAccumulator>(
        { flatAndHome: [], entrance: [] },
    );

    const { resetCamerasDispatch } = useRequisitionContext();

    const { accessToken, providerId } = useSelector((state: TState) => ({
        accessToken: state.auth.accessToken,
        providerId: selectProviderId(state),
    }));

    const videoCamerasDataAll = useMemo(() => ({
        [ELocation.flat]: videoCamerasData.flatAndHome,
        [ELocation.entrance]: videoCamerasData.entrance,
        [ELocation.area]: videoCamerasData.entrance,
        [ELocation.home]: videoCamerasData.flatAndHome,
    }), [videoCamerasData]);

    const currentCamerasData = videoCamerasDataAll[ELocation[activeTabLocation]];

    useEffect(() => {
        (async() => {
            const items = await getVideocontrolItems(providerId, accessToken);
            if (items.length) {
                const videoCamerasDataUpdated = items.reduce(
                    (acc: TVideoCamerasDataAccumulator, item: IShopItem) => {
                        const isInside = item?.props?.[0]?.value === 'inside';
                        if (isInside) {
                            acc.flatAndHome.push(item);
                        } else {
                            acc.entrance.push(item);
                        }
                        return acc;
                    }, { flatAndHome: [], entrance: [] },
                );

                setVideoCamerasData(videoCamerasDataUpdated);
            }
        })();
    }, [accessToken, providerId]);

    const setActiveTabToggleWrap = (activeTab: string) => {
        setCurrentLocationData(dataLocation[activeTab]);
        setActiveTabToggleLoc(activeTab);
        setActiveTabLocation(dataLocation[activeTab][defaultTab].id);
        resetCamerasDispatch();
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.landing,
            action: 'click_on_tab',
            label: dataTabsForLoc.leftText === activeTab ? 'apartment' : 'village',
        });
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.landing,
            action: 'chose installation',
            label: dataLocation[activeTab][defaultTab].id,
        });
    };

    const setActiveTabWrap = (id: ELocation) => {
        setActiveTabLocation(id);
        resetCamerasDispatch();
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.landing,
            action: 'chose installation',
            label: id,
        });
    };

    return (
        <ST.Container>
            <ST.StepContainer>
                <StepTitle
                    step={'Шаг 1.'}
                    stepText={'Выберите место установки камеры'}
                >
                    <ST.Title ref={ref}>
                        Как установить видеокамеру в квартире или частном доме
                    </ST.Title>
                </StepTitle>
                <Toggle
                    onChangeCallback={setActiveTabToggleWrap}
                    activeTab={activeTabToggleLoc}
                    textsValues={dataTabsForLoc}
                    colorTab={COLORS.Logo}
                    isDifferentColorText={false}
                />
                <LocationTabsBlock
                    data={currentLocationData}
                    setActiveTab={setActiveTabWrap}
                    activeTab={activeTabLocation}
                />
            </ST.StepContainer>
            <ST.StepContainer>
                <StepTitle
                    step={'Шаг 2.'}
                    stepText={'Выберите камеру'}
                    subText={'Беспроводные Wi-Fi камеры: распознавание движения, съемка в качестве Full HD'}
                />
                <NukaWrapper
                    dataLength={currentCamerasData.length}
                    data={currentCamerasData}
                    Card={CameraCard}
                    widthCard={widthCard}
                    activeTabLocation={activeTabLocation}
                    domain={props.domain}
                />
            </ST.StepContainer>
            <ST.StepContainer>
                <StepTitle
                    step={'Шаг 3.'}
                    stepText={'Выберите тариф'}
                    subText={'Тарифы отличаются временем хранения записи. Выберите подходящий.'}
                />
                <NukaWrapper
                    dataLength={dataCameraPlans.length}
                    data={dataCameraPlans}
                    Card={PlanCard}
                    widthCard={widthCard}
                />
            </ST.StepContainer>
        </ST.Container>
    );
});

StepsInstall.displayName = 'StepsInstall';
export default StepsInstall;
